import { useSyncExternalStore } from 'react';

export type NetworkQuality = 'good' | 'degraded' | 'offline';
export type EffectiveType = '4g' | '3g' | '2g' | 'slow-2g';

export interface BrowserNetworkStatus {
  isOnline: boolean;
  effectiveType: EffectiveType | null;
  downlink: number | null;
  rtt: number | null;
  saveData: boolean;
  quality: NetworkQuality;
}

// Extend Navigator for Network Information API (not in all browsers)
interface NetworkInformation extends EventTarget {
  effectiveType?: EffectiveType;
  downlink?: number;
  rtt?: number;
  saveData?: boolean;
  addEventListener(type: 'change', listener: () => void): void;
  removeEventListener(type: 'change', listener: () => void): void;
}

declare global {
  interface Navigator {
    connection?: NetworkInformation;
  }
}

function deriveQuality(isOnline: boolean, conn: NetworkInformation | undefined): NetworkQuality {
  if (!isOnline) return 'offline';
  if (!conn) return 'good';
  if (conn.effectiveType === 'slow-2g' || conn.effectiveType === '2g') return 'degraded';
  if (conn.downlink !== undefined && conn.downlink < 0.5) return 'degraded';
  if (conn.rtt !== undefined && conn.rtt > 500) return 'degraded';
  return 'good';
}

// Cached snapshot — useSyncExternalStore compares by reference (Object.is),
// so we must return the same object when nothing changed.
let cachedSnapshot: BrowserNetworkStatus | null = null;

function getSnapshot(): BrowserNetworkStatus {
  const conn = navigator.connection;
  const isOnline = navigator.onLine;
  const effectiveType = (conn?.effectiveType as EffectiveType) ?? null;
  const downlink = conn?.downlink ?? null;
  const rtt = conn?.rtt ?? null;
  const saveData = conn?.saveData ?? false;
  const quality = deriveQuality(isOnline, conn);

  if (
    cachedSnapshot &&
    cachedSnapshot.isOnline === isOnline &&
    cachedSnapshot.effectiveType === effectiveType &&
    cachedSnapshot.downlink === downlink &&
    cachedSnapshot.rtt === rtt &&
    cachedSnapshot.saveData === saveData &&
    cachedSnapshot.quality === quality
  ) {
    return cachedSnapshot;
  }

  cachedSnapshot = { isOnline, effectiveType, downlink, rtt, saveData, quality };
  return cachedSnapshot;
}

// Server-side fallback
const serverSnapshot: BrowserNetworkStatus = {
  isOnline: true,
  effectiveType: null,
  downlink: null,
  rtt: null,
  saveData: false,
  quality: 'good',
};

function subscribe(onStoreChange: () => void): () => void {
  window.addEventListener('online', onStoreChange);
  window.addEventListener('offline', onStoreChange);
  navigator.connection?.addEventListener('change', onStoreChange);

  return () => {
    window.removeEventListener('online', onStoreChange);
    window.removeEventListener('offline', onStoreChange);
    navigator.connection?.removeEventListener('change', onStoreChange);
  };
}

/**
 * Hook that exposes the browser's own network status.
 * Uses navigator.onLine and the Network Information API to detect
 * offline state and degraded cellular conditions.
 */
export function useBrowserNetworkStatus(): BrowserNetworkStatus {
  return useSyncExternalStore(subscribe, getSnapshot, () => serverSnapshot);
}
